import React from "react";
import { Modal, Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { Icon } from "@/shared/components/Icon/Icon";

const StyledModal = styled(Modal)`
	.ant-modal-content {
		background: #ffebef !important;
		border-radius: 20px !important;
		padding: 0 !important;
		box-shadow: 0px 12px 24px 0px #d800273d !important;
	}
	.ant-modal-mask {
		background-color: rgba(0, 0, 0, 0.45) !important;
	}
`;

const ModalContent = styled.div`
	position: relative;
	padding: 28px 28px 24px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	width: 100%;
`;

const CloseButton = styled.button`
	position: absolute;
	top: 8px;
	right: 8px;
	width: 32px;
	height: 32px;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: #555;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;

	&:hover:not(:disabled) {
		background: rgba(0, 0, 0, 0.06);
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
`;

const Title = styled.div`
	font-size: 20px;
	font-weight: 600;
	color: #1a1a1a;
	margin-bottom: 12px;
	margin-top: 16px;
	line-height: 1.35;
	width: 100%;
	text-align: left;
`;

const Description = styled.div`
	font-size: 15px;
	color: #555;
	line-height: 1.55;
	margin-bottom: 28px;
	width: 100%;
	text-align: left;
`;

const ButtonGroup = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 16px;
	align-items: center;
	width: 100%;
	font-size: 16px;
	align-self: stretch;
`;

const CancelButton = styled(Button)`
	flex: 1;
	height: 48px;
	border-radius: 999px;
	font-size: 16px;
	font-weight: 600;
	border: 2px solid #2e1a89;
	color: #2e1a89;
	background: #fff;

	&:hover,
	&:focus-visible {
		box-shadow: inset 0 0 0 2px #3a0ca3 !important;
		border-color: #3a0ca3 !important;
		color: #3a0ca3 !important;
	}
`;

const DeleteButton = styled(Button)`
	flex: 1;
	height: 48px;
	border-radius: 999px;
	font-weight: 600;
	background: #d30024 !important;
	border: none;
	color: #fff !important;

	&:hover {
		background: #d30024 !important;
		opacity: 0.9;
	}
`;

interface DeleteConfirmModalProps {
	open: boolean;
	onCancel: () => void;
	onConfirm: () => void;
	loading?: boolean;
	campaignName?: string;
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
	open,
	onCancel,
	onConfirm,
	loading = false,
	campaignName = "this campaign",
}) => {
	return (
		<StyledModal
			open={open}
			onCancel={onCancel}
			footer={null}
			closable={false}
			centered
			width={520}
			maskClosable={!loading}
			keyboard={!loading}
		>
			<ModalContent>
				<CloseButton aria-label="Close" onClick={onCancel} disabled={loading}>
					<CloseOutlined />
				</CloseButton>
				<Icon src="/icon/trash.svg" size={24} color="#D30024" className="delete-modal-icon" />
				<Title>Delete {campaignName}</Title>
				<Description>Are you sure you want to delete this campaign? This action cannot be undone.</Description>
				<ButtonGroup>
					<CancelButton size="large" onClick={onCancel} disabled={loading}>
						Cancel
					</CancelButton>
					<DeleteButton type="primary" size="large" loading={loading} onClick={onConfirm}>
						Delete
					</DeleteButton>
				</ButtonGroup>
			</ModalContent>
		</StyledModal>
	);
};
